import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * SectorRequestDialog — opened from the "Not seeing your sector?" strip on
 * /pay-per-call. Captures a lightweight demand signal (sector name, role,
 * volume, contact) that lands in the admin sector-demand dashboard and
 * fires an ops email in real-time.
 *
 * Design language matches the pillars/form dark orbital aesthetic already
 * on /pay-per-call.
 */
export default function SectorRequestDialog({ open, onOpenChange, defaultRole = "buyer" }) {
  const [form, setForm]  = useState({ sector_name: "", role: defaultRole, volume_hint: "", email: "", name: "", company: "", notes: "" });
  const [busy, setBusy]  = useState(false);
  const [sent, setSent]  = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setRole  = (r) => setForm((f) => ({ ...f, role: r }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sector_name.trim() || !form.email.trim()) {
      toast.error("Sector name and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/pay-per-call/sector-request", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again in a moment");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (o) => {
    if (!o) {
      // Reset on close
      setTimeout(() => {
        setSent(false);
        setForm({ sector_name: "", role: defaultRole, volume_hint: "", email: "", name: "", company: "", notes: "" });
      }, 300);
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg bg-[#0a1124] border-white/10 text-white overflow-hidden p-0 max-h-[92dvh] w-[calc(100vw-1rem)] sm:w-full"
        data-testid="sector-request-dialog"
      >
        {/* Ambient accents */}
        <div className="absolute -top-24 -right-16 w-52 h-52 rounded-full bg-[#0055FF]/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-52 h-52 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />

        {/* Scroll wrapper — lets tall content flow safely on phones */}
        <div className="relative overflow-y-auto max-h-[92dvh] overscroll-contain">
        <div className="relative p-5 sm:p-7">
          <DialogHeader>
            <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
              <Sparkles className="size-3" />
              Request a new sector
            </div>
            <DialogTitle className="mt-2 text-2xl tracking-[-0.02em] font-semibold text-white">
              Which sector should we launch next?
            </DialogTitle>
            <DialogDescription className="text-white/55 leading-relaxed">
              Tell us the vertical you produce or want to buy in. We&apos;ll add it to our launch queue and come back within one business day.
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="text-center py-8" data-testid="sector-request-success">
              <span className="mx-auto grid place-items-center size-12 rounded-full bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30">
                <CheckCircle2 className="size-6" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-white">Signal received.</h3>
              <p className="mt-1.5 text-[13.5px] text-white/60 leading-relaxed max-w-sm mx-auto">
                Thanks — <span className="text-white/85">{form.sector_name}</span> is on the launch queue. We&apos;ll reply within one business day.
              </p>
              <Button
                onClick={() => handleClose(false)}
                className="mt-6 rounded-full bg-white text-slate-900 hover:bg-white/90"
                data-testid="sector-request-close"
              >
                Close <ArrowRight className="size-3.5 ml-1" />
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              {/* Role toggle */}
              <div className="relative inline-flex w-full rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="sector-request-role">
                <span
                  className="absolute top-1 bottom-1 left-1 rounded-full transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  style={{
                    width: "calc(50% - 4px)",
                    transform: form.role === "buyer" ? "translateX(0)" : "translateX(100%)",
                    background: form.role === "buyer"
                      ? "linear-gradient(135deg,#0055FF 0%,#3d7bff 100%)"
                      : "linear-gradient(135deg,#1e293b 0%,#334155 100%)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setRole("buyer")}
                  className={`relative z-10 flex-1 h-9 rounded-full text-[12.5px] font-semibold transition-colors ${form.role === "buyer" ? "text-white" : "text-white/50 hover:text-white/80"}`}
                  data-testid="sector-request-role-buyer"
                >
                  I want to buy calls
                </button>
                <button
                  type="button"
                  onClick={() => setRole("publisher")}
                  className={`relative z-10 flex-1 h-9 rounded-full text-[12.5px] font-semibold transition-colors ${form.role === "publisher" ? "text-white" : "text-white/50 hover:text-white/80"}`}
                  data-testid="sector-request-role-publisher"
                >
                  I run a call centre
                </button>
              </div>

              <div>
                <Label htmlFor="sr-sector" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                  Sector name *
                </Label>
                <Input
                  id="sr-sector"
                  data-testid="sr-sector"
                  value={form.sector_name}
                  onChange={setField("sector_name")}
                  placeholder="e.g. Pet Insurance, Estate Planning, Cyber Insurance"
                  className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="sr-volume" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                    Volume hint
                  </Label>
                  <Input
                    id="sr-volume"
                    data-testid="sr-volume"
                    value={form.volume_hint}
                    onChange={setField("volume_hint")}
                    placeholder="e.g. 200 calls/mo"
                    className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="sr-email" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                    Work email *
                  </Label>
                  <Input
                    id="sr-email"
                    data-testid="sr-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@company.co.uk"
                    className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="sr-name" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                    Name
                  </Label>
                  <Input
                    id="sr-name"
                    data-testid="sr-name"
                    value={form.name}
                    onChange={setField("name")}
                    placeholder="Marcus Ali"
                    className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="sr-company" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                    Company
                  </Label>
                  <Input
                    id="sr-company"
                    data-testid="sr-company"
                    value={form.company}
                    onChange={setField("company")}
                    placeholder="Company Ltd"
                    className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="sr-notes" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">
                  Notes
                </Label>
                <Textarea
                  id="sr-notes"
                  data-testid="sr-notes"
                  value={form.notes}
                  onChange={setField("notes")}
                  placeholder="Timeline, existing volume, compliance needs, anything else…"
                  className="mt-1.5 min-h-[80px] resize-none bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                data-testid="sector-request-submit"
                className="w-full rounded-full h-11 bg-gradient-to-r from-[#0055FF] to-[#3d7bff] hover:from-[#0044CC] hover:to-[#0055FF] font-semibold gap-1.5 shadow-[0_12px_32px_rgba(0,85,255,0.5)] border border-white/10"
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-4" /> Send signal</>}
              </Button>
              <p className="text-[10.5px] text-white/40 text-center leading-relaxed">
                Real-time signal to the ops desk. We reply within one business day.
              </p>
            </form>
          )}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
